import React, { useRef, useState } from "react";
import { X, Upload, GripVertical } from "lucide-react";
import { api } from "../lib/api";

export default function AdditionalPhotos({ cardId, images, onChange }) {
  const inputRef = useRef(null);
  const dragIndex = useRef(null);
  const [uploading, setUploading] = useState(false);

  const fullUrl = (u) => (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

  const handleUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    const formData = new FormData();
    Array.from(fileList).forEach((f) => formData.append("files", f));
    try {
      const res = await api.post(`/cards/${cardId}/photos`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.additional_images);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageUrl) => {
    const res = await api.post(`/cards/${cardId}/photos/delete`, { image_url: imageUrl });
    onChange(res.data.additional_images);
  };

  const handleDrop = async (dropIndex) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === dropIndex) return;
    const reordered = [...images];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(dropIndex, 0, moved);
    onChange(reordered);
    await api.post(`/cards/${cardId}/photos/reorder`, { images: reordered });
  };

  return (
    <div data-testid="additional-photos-gallery" className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-zinc-500">Condition Photos</span>
        <button
          data-testid="additional-photos-upload-button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 text-xs text-[#A78BFA] hover:text-white transition-colors disabled:opacity-40"
        >
          <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading..." : "Add photos"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          data-testid="additional-photos-input"
          className="hidden"
          onChange={(e) => { handleUpload(e.target.files); e.target.value = ""; }}
        />
      </div>

      {images.length === 0 ? (
        <p data-testid="additional-photos-empty" className="text-xs text-zinc-600 border border-dashed border-white/10 rounded-lg p-4 text-center">
          No extra condition photos yet — add close-ups of edges, corners or surface for buyers.
        </p>
      ) : (
        <div className="grid grid-cols-4 gap-2">
          {images.map((img, i) => (
            <div
              key={img}
              data-testid={`additional-photo-${i}`}
              draggable
              onDragStart={() => (dragIndex.current = i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(i)}
              className="relative group aspect-square rounded-lg overflow-hidden border border-white/10 cursor-grab active:cursor-grabbing"
            >
              <img src={fullUrl(img)} alt={`condition ${i + 1}`} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors" />
              <GripVertical className="absolute bottom-1 left-1 w-3.5 h-3.5 text-white/70 opacity-0 group-hover:opacity-100 transition-opacity" />
              <button
                data-testid={`additional-photo-delete-${i}`}
                onClick={() => handleDelete(img)}
                className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/80"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
